"use client";

import Link from "next/link";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { track } from "../_analytics/track";
import {
  aantalStappen,
  kwalificatieOpslagsleutel,
  leidRouteAf,
  maakKwalificatie,
  prijsnoot,
  routeUitkomsten,
  stappen,
  type Antwoorden,
  type Projectroute,
  type Stapsleutel,
} from "../_data/leadkwalificatie";

/**
 * De projectrouter: zes vragen, één per scherm, elk met radiokaarten in plaats
 * van een keuzelijst. Geen naam, geen e-mailadres, geen verplicht veld — de
 * bezoeker kwalificeert zichzelf en pas de uitkomst leidt naar een formulier.
 */
export function ProjectRouter({ bronPagina = "/projectrouter" }: { bronPagina?: string }) {
  const [index, setIndex] = useState(0);
  const [antwoorden, setAntwoorden] = useState<Antwoorden>({ plaats: "" });
  const [klaar, setKlaar] = useState(false);

  /** Het starttijdstip hoort in de uitkomst, dus het is state en geen ref. */
  const [gestartOp, setGestartOp] = useState<string | null>(null);
  const gestart = useRef(false);
  const routeGemeld = useRef<Projectroute | null>(null);
  const kopRef = useRef<HTMLHeadingElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  /** De eerste interactie start de meting; daarna is dit een no-op. */
  const meldStart = useCallback(() => {
    if (gestart.current) return;
    gestart.current = true;
    setGestartOp(new Date().toISOString());
    track("project_router_started", { component: "project-router", entry_page: bronPagina });
  }, [bronPagina]);

  const stap = stappen[index];
  const route = useMemo(
    () => (antwoorden.kavelstatus && antwoorden.ontwerpstatus ? leidRouteAf(antwoorden) : null),
    [antwoorden],
  );

  // Zodra de route uit stap 1 en 2 volgt, is dat een routekeuze — ook al staat
  // de bezoeker nog midden in de reeks. Bij een correctie meldt hij opnieuw.
  useEffect(() => {
    if (!route || routeGemeld.current === route) return;
    routeGemeld.current = route;
    track("route_selected", { component: "project-router", selected_route: route, source: "router" });
  }, [route]);

  function kies(sleutel: Stapsleutel, waarde: string) {
    meldStart();
    setAntwoorden((vorige) => ({ ...vorige, [sleutel]: waarde }));

    // Even laten staan zodat de bezoeker zijn keuze bevestigd ziet worden.
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (index + 1 < aantalStappen) setIndex(index + 1);
      else setKlaar(true);
    }, 260);
  }

  function terug() {
    if (klaar) { setKlaar(false); setIndex(aantalStappen - 1); return; }
    if (index > 0) setIndex(index - 1);
  }

  function naarStap(doel: number) {
    setKlaar(false);
    setIndex(doel);
  }

  // Bij elke stapwissel de vraag aankondigen, zodat toetsenbord en schermlezer
  // niet bovenaan de pagina achterblijven.
  useEffect(() => {
    if (gestart.current) kopRef.current?.focus();
  }, [index, klaar]);

  const kwalificatie = useMemo(
    () =>
      klaar
        ? maakKwalificatie(antwoorden, {
            bron_pagina: bronPagina,
            bron_component: "project-router",
            gestart_op: gestartOp ?? new Date().toISOString(),
          })
        : null,
    [klaar, antwoorden, bronPagina, gestartOp],
  );

  useEffect(() => {
    if (!kwalificatie) return;
    track("project_router_completed", {
      component: "project-router",
      selected_route: kwalificatie.route,
      rol: kwalificatie.rol,
      steps_completed: aantalStappen,
    });
    try {
      // Alleen deze sessie. Opslaan en doorsturen volgt in een later ticket.
      window.sessionStorage.setItem(kwalificatieOpslagsleutel, JSON.stringify(kwalificatie));
    } catch {
      // Privémodus of geblokkeerde opslag mag de uitkomst niet wegnemen.
    }
  }, [kwalificatie]);

  if (kwalificatie) {
    const uitkomst = routeUitkomsten[kwalificatie.route];
    const pakket = kwalificatie.pakketindicatie;

    return (
      <div className="pr-shell pr-outcome">
        <p className="section-label">{uitkomst.label}</p>
        <h2 className="pr-outcome-title" ref={kopRef} tabIndex={-1}>{uitkomst.titel}</h2>
        <p className="pr-outcome-text">{uitkomst.tekst}</p>

        <div className="pr-outcome-cards">
          <article className="pr-outcome-card">
            <span className="pr-eyebrow">Je volgende stap</span>
            <h3>{uitkomst.cta}</h3>
            <p>Stuur wat je al hebt. Je hoort wat kan, wat nog ontbreekt en wat de stap daarna is.</p>
            <Link className="button button-primary" href={`/start?route=${kwalificatie.route}`}>
              {uitkomst.cta} <span aria-hidden="true">→</span>
            </Link>
            <Link className="text-link" href={uitkomst.href}>
              Lees eerst over deze route <span aria-hidden="true">→</span>
            </Link>
          </article>

          <article className="pr-outcome-card">
            <span className="pr-eyebrow">Pakketindicatie</span>
            {pakket ? (
              <>
                <h3>{pakket}</h3>
                <p>Dit past bij het budget dat je noemde. Het is een indicatie, geen offerte: wat erin zit, staat op de prijspagina.</p>
                <Link className="text-link" href="/manieren-van-werken">
                  Bekijk wat erin zit <span aria-hidden="true">→</span>
                </Link>
              </>
            ) : (
              <>
                <h3>Nog geen indicatie</h3>
                <p>Je budget staat nog open. Reken eerst met de richtprijzen per pakket, dan weet je waar je uitkomt.</p>
                <Link className="text-link" href="/manieren-van-werken">
                  Bekijk pakketten en richtprijzen <span aria-hidden="true">→</span>
                </Link>
              </>
            )}
            <p className="pr-note">{prijsnoot}</p>
          </article>
        </div>

        <div className="pr-recap">
          <span className="pr-eyebrow">Dit heb je ingevuld</span>
          <dl>
            {stappen.map((item, positie) => {
              const waarde = antwoorden[item.sleutel];
              const keuze = item.keuzes.find((optie) => optie.waarde === waarde);
              return (
                <div className="pr-recap-row" key={item.sleutel}>
                  <dt>{item.eyebrow}</dt>
                  <dd>
                    <span>{keuze?.label ?? "—"}</span>
                    <button type="button" onClick={() => naarStap(positie)}>Wijzig</button>
                  </dd>
                </div>
              );
            })}
            {antwoorden.plaats.trim() && (
              <div className="pr-recap-row">
                <dt>Plaats</dt>
                <dd><span>{antwoorden.plaats.trim()}</span><button type="button" onClick={() => naarStap(0)}>Wijzig</button></dd>
              </div>
            )}
          </dl>
        </div>

        <div className="pr-outcome-links">
          {uitkomst.verder.map((link) => (
            <Link className="button button-dark" key={link.href} href={link.href}>{link.label}</Link>
          ))}
        </div>
      </div>
    );
  }

  const voortgang = Math.round((index / aantalStappen) * 100);

  return (
    <div className="pr-shell">
      <div className="pr-progress">
        <div className="pr-progress-head">
          <span className="pr-eyebrow">Stap {index + 1} van {aantalStappen}</span>
          <span className="pr-progress-label">{stap.eyebrow}</span>
        </div>
        <div
          className="pr-progress-bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={aantalStappen}
          aria-valuenow={index + 1}
          aria-valuetext={`Stap ${index + 1} van ${aantalStappen}: ${stap.eyebrow}`}
        >
          <span style={{ width: `${Math.max(voortgang, 6)}%` }} />
        </div>
      </div>

      <h2 className="pr-question" ref={kopRef} tabIndex={-1}>{stap.vraag}</h2>
      <p className="pr-why">{stap.waarom}</p>

      {stap.sleutel === "kavelstatus" && (
        <div className="pr-field">
          <label htmlFor="pr-plaats">In welke plaats? <span>Optioneel</span></label>
          <input
            id="pr-plaats"
            name="plaats"
            type="text"
            maxLength={160}
            autoComplete="address-level2"
            placeholder="Bijvoorbeeld Naarden"
            value={antwoorden.plaats}
            onChange={(event) => { meldStart(); setAntwoorden((vorige) => ({ ...vorige, plaats: event.target.value })); }}
          />
        </div>
      )}

      <fieldset className="pr-choices">
        <legend className="sr-only">{stap.vraag}</legend>
        {stap.keuzes.map((keuze) => {
          const gekozen = antwoorden[stap.sleutel] === keuze.waarde;
          return (
            <label className={`pr-choice${gekozen ? " is-selected" : ""}`} key={keuze.waarde}>
              <input
                type="radio"
                name={stap.sleutel}
                value={keuze.waarde}
                checked={gekozen}
                onChange={() => kies(stap.sleutel, keuze.waarde)}
              />
              <span className="pr-choice-body">
                <strong>{keuze.label}</strong>
                <span>{keuze.toelichting}</span>
              </span>
              <span className="pr-choice-mark" aria-hidden="true" />
            </label>
          );
        })}
      </fieldset>

      <div className="pr-actions">
        <button className="button button-text" type="button" onClick={terug} disabled={index === 0}>
          <span aria-hidden="true">←</span> Terug
        </button>
        <span className="pr-actions-note">
          {route ? `Route tot nu toe: ${routeUitkomsten[route].label.toLowerCase()}` : "Kies een antwoord om verder te gaan"}
        </span>
      </div>
    </div>
  );
}
